"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import ContactForm from "@/app/Components/ContactForm";
import Head from "next/head";
import { MessageCircle, Instagram } from "lucide-react";
import { motion } from "framer-motion";

const faqs = [
  {
    question: "How long does a renovation typically take?",
    answer: "Typical renovation timelines vary but we provide clear estimates upfront to keep your project on track.",
  },
  {
    question: "How much do design services typically cost?",
    answer: "Costs depend on scope and scale; we tailor packages to your budget and goals.",
  },
  {
    question: "What is the benefit of hiring a designer?",
    answer: "Designers bring expertise in aesthetics, functionality, and project management for a seamless experience.",
  },
  {
    question: "When can you start on my design project/renovation?",
    answer: "We typically schedule new projects 2-4 weeks in advance, but timelines may vary.",
  },
];

const AccordionItem = ({ faq, isOpen, onToggle }) => {
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (isOpen) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setMaxHeight("0px");
    }
  }, [isOpen]);

  return (
    <div className="mb-4 border-b border-[#A65D5D] cursor-pointer select-none" onClick={onToggle}>
      <h4 className="font-semibold text-lg flex justify-between items-center text-[#A65D5D] py-3">
        <span>{faq.question}</span>
        <span className="ml-2 text-xl">{isOpen ? "−" : "+"}</span>
      </h4>
      <div
        ref={contentRef}
        style={{ maxHeight, overflow: "hidden", transition: "max-height 0.4s ease" }}
      >
        <p className="mt-0 mb-6 text-[#6B4F4F]">{faq.answer}</p>
      </div>
    </div>
  );
};

const Contact = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="min-h-screen bg-primaryColor-darker/50 px-4 py-12 sm:px-6 sm:py-20 flex flex-col">
      {/* ✅ SEO Head Tags */}
      <Head>
        <title>Contact — Design Nook Interiors</title>
        <meta
          name="description"
          content="Get in touch with Design Nook for personalized interior design and renovation services. Let's bring your dream space to life."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta charSet="UTF-8" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://designook.in/contact" />
      </Head>

      {/* Heading */}
      <motion.h1
        className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#A65D5D] text-center mb-12 sm:mb-16"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        Let’s Bring Your Dream Space to Life — Design Nook
      </motion.h1>

      <div className="max-w-7xl mx-auto flex flex-col  p-10  sm:flex-row gap-8 sm:gap-16 items-start">
        {/* Left - Info + FAQ + CTA */}
        <motion.div
          className="w-full sm:w-1/2 space-y-6 sm:space-y-8 text-[#3E2F2F] "
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {/* Contact Info */}
          <div className="px-4 py-6 sm:px-6 md:px-8 lg:px-12 max-w-3xl mx-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-4 sm:mb-5 text-center">
              Get in touch about your project
            </h2>

            <h3 className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#A65D5D] mb-4 sm:mb-6 text-center">
              LET’S CREATE
            </h3>

            <div className="space-y-3 sm:space-y-4 text-sm sm:text-base">
              <p className="font-semibold">
                Email:{" "}
                <a href="mailto:designook.id@gmail.com" className="text-[#A65D5D] hover:underline break-words">
                  designook.id@gmail.com
                </a>
              </p>

              <p className="font-semibold">
                Phone:{" "}
                <a href="tel:+917017208147" className="text-[#A65D5D] hover:underline">
                  +91 70172-08147
                </a>
                {" , "}
                <a href="tel:+917341109977" className="text-[#A65D5D] hover:underline">
                  +91 73411-09977
                </a>
              </p>

              <p className="font-semibold">
                Address:{" "}
                <span className="text-[#A65D5D]">
                  CPM 09, Second Floor, Central Plaza, EMAAR MGF Sector-105, Mohali, Punjab, India.
                </span>
              </p>
            </div>
          </div>

          {/* FAQ Accordion */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                faq={faq}
                isOpen={openIndex === index}
                onToggle={() => toggleAccordion(index)}
              />
            ))}
          </motion.div>

          {/* CTA Buttons */}
         {/* CTA Buttons */}
<div className="flex flex-col sm:flex-col md:flex-row gap-4 mt-6 w-full">
  {/* WhatsApp Button */}
  <motion.a
    href="https://wa.me/7017208147"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Chat on WhatsApp"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    viewport={{ once: true }}
    className="flex items-center justify-center gap-2 bg-[#A65D5D] hover:bg-[#8C4A4A] text-white text-base sm:text-lg font-semibold px-6 py-3 rounded-lg shadow-md transition-colors duration-300 w-full md:w-auto"
  >
    <MessageCircle className="w-5 h-5" />
    Chat on WhatsApp
  </motion.a>

  {/* Instagram Button */}
  <motion.a
    href="https://www.instagram.com/design_nook.interiors/"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Follow on Instagram"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: 0.1 }}
    viewport={{ once: true }}
    className="flex items-center justify-center gap-2 bg-[#A65D5D] hover:bg-[#8C4A4A] text-white text-base sm:text-lg font-semibold px-6 py-3 rounded-lg shadow-md transition-colors duration-300 w-full md:w-auto"
  >
    <Instagram className="w-5 h-5" />
    Follow us on Instagram
  </motion.a>
</div>
        </motion.div>

        {/* Right - Image */}
        <motion.div
          className="w-full sm:w-1/2 flex flex-col items-center   sm:items-center gap-6 sm:gap-8"
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="w-full max-w-xs sm:max-w-md relative rounded-lg overflow-hidden shadow-lg">
            <Image
  src="/contact.webp"
  alt="Contact us"
  width={500}
  height={350}
  sizes="(max-width: 640px) 100vw, 50vw"
  srcSet="/contact-sm.webp 320w, /contact.webp 1024w"
  style={{ objectFit: "contain" }}
  priority
  fetchPriority="high"
  
/>
          </div>

          <p className="italic text-[#6B4F4F] text-center sm:text-left text-xs sm:text-sm">
            * Please allow 2-3 days for a reply via email.
          </p>
        </motion.div>
      </div>

      {/* Contact Form */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <ContactForm />
      </motion.div>
    </section>
  );
};

export default Contact;
